import { weekdaySchema } from '@gamble-chess/contracts';
import type {
  Balance,
  Counterparty,
  CounterpartyRef,
  GameResult,
  GuardEffect,
  Platform,
  RatingTier,
  RuleGuard,
  StakePolicyKind,
  StakePolicyResponse,
  StakeUnit,
  TimeClass,
  WagerAction,
  WagerResponse,
  WagerStatus,
  Weekday,
  WorkSessionResponse,
} from '@gamble-chess/contracts';

export const platformLabels: Readonly<Record<Platform, string>> = {
  'chess.com': 'chess.com',
  lichess: 'lichess',
};

/** "carol_cc on chess.com": one account, wherever it is offered as a choice. */
export function describeAccount({
  username,
  platform,
}: {
  readonly username: string;
  readonly platform: Platform;
}): string {
  return `${username} on ${platformLabels[platform]}`;
}

/** Why there is nothing to pick: an empty select explains nothing (ADR-0024). */
export function describeNoAccountToChallenge(displayName: string, platform: Platform): string {
  return `${displayName} tracks no ${platformLabels[platform]} account. Pick another of your accounts, or ask them to add one.`;
}

export const resultLabels: Readonly<Record<GameResult, string>> = {
  win: 'Win',
  draw: 'Draw',
  loss: 'Loss',
};

export const timeClassLabels: Readonly<Record<TimeClass, string>> = {
  bullet: 'Bullet',
  blitz: 'Blitz',
  rapid: 'Rapid',
  classical: 'Classical',
  daily: 'Daily',
};

const dateTime = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });

export function formatDateTime(iso: string): string {
  return dateTime.format(new Date(iso));
}

const unitNames: Readonly<Record<StakeUnit, { readonly one: string; readonly many: string }>> = {
  minutes: { one: 'minute', many: 'minutes' },
  points: { one: 'point', many: 'points' },
};

export const unitLabels: Readonly<Record<StakeUnit, string>> = {
  minutes: 'Minutes',
  points: 'Points',
};

/** "10 minutes", "1 point": the size of an amount, whatever its sign. */
export function formatAmount(amount: number, unit: StakeUnit): string {
  const size = Math.abs(amount);
  return `${String(size)} ${size === 1 ? unitNames[unit].one : unitNames[unit].many}`;
}

export const policyKindLabels: Readonly<Record<StakePolicyKind, string>> = {
  fixed: 'Fixed',
  'rating-tiered': 'Scaled by rating gap',
};

export function describePolicy(policy: StakePolicyResponse): string {
  const stake = formatAmount(policy.stake.amount, policy.stake.unit);
  return policy.kind === 'fixed' ? stake : `${stake}, scaled by rating gap`;
}

/** One band in words, from the loser's side (ADR-0017). */
export function describeTier({ minGap, maxGap, multiplier }: RatingTier): string {
  const times = `×${String(multiplier)}`;
  if (minGap !== null && maxGap === null) {
    return `${times} when the winner was rated ${String(minGap)} or more below the loser`;
  }
  if (minGap === null && maxGap !== null) {
    return `${times} when the winner was rated ${String(-maxGap)} or more above the loser`;
  }
  if (minGap !== null && maxGap !== null) {
    return `${times} when the ratings were within ${String(maxGap)} points`;
  }
  return `${times} always`;
}

export function counterpartyName(counterparty: Counterparty): string {
  switch (counterparty.kind) {
    case 'self':
      return 'yourself';
    case 'user':
      return counterparty.user.displayName;
    case 'opponent':
      return counterparty.name;
  }
}

/**
 * "You owe Bob", "Bob owes you", "Redeemed": which way an amount points and between whom,
 * without saying how much — for a display that shows the size on its own line.
 */
export function describeDirection(counterparty: Counterparty, amount: number): string {
  if (counterparty.kind === 'self') {
    return amount > 0 ? 'You owe yourself' : 'Redeemed';
  }
  const name = counterpartyName(counterparty);
  return amount > 0 ? `You owe ${name}` : `${name} owes you`;
}

/**
 * "You owe Bob 20 minutes", "Bob owes you 20 minutes", "Redeemed 5 minutes": an amount from
 * the ledger in words, never a bare signed number.
 */
export function describeDebt(counterparty: Counterparty, amount: number, unit: StakeUnit): string {
  return `${describeDirection(counterparty, amount)} ${formatAmount(amount, unit)}`;
}

export function describeBalance({ counterparty, unit, amount }: Balance): string {
  return describeDebt(counterparty, amount, unit);
}

/** One key per counterparty, for React lists and for picking one in a select. */
export function counterpartyKey(counterparty: Counterparty): string {
  switch (counterparty.kind) {
    case 'self':
      return 'self';
    case 'user':
      return `user:${counterparty.user.id}`;
    case 'opponent':
      return `opponent:${counterparty.name}`;
  }
}

/** What a request calls the balance the UI is showing. */
export function counterpartyRef(counterparty: Counterparty): CounterpartyRef {
  switch (counterparty.kind) {
    case 'self':
      return { kind: 'self' };
    case 'user':
      return { kind: 'user', userId: counterparty.user.id };
    case 'opponent':
      return { kind: 'opponent', name: counterparty.name };
  }
}

/** "your own debt", "what you owe Bob": what a work session is working off. */
export function describeWorkTarget(counterparty: Counterparty): string {
  return counterparty.kind === 'self'
    ? 'your own debt'
    : `what you owe ${counterpartyName(counterparty)}`;
}

/** "12:05": a running timer, counting up in minutes and seconds. */
export function formatElapsed(milliseconds: number): string {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** A finished session in words, saying so when the debt could not take all of it. */
export function describeWorkSession({ counterparty, finish }: WorkSessionResponse): string {
  if (finish === null) {
    return `Working off ${describeWorkTarget(counterparty)}`;
  }
  const worked = `Worked ${formatAmount(finish.minutes, 'minutes')} off ${describeWorkTarget(counterparty)}`;
  return finish.credited === finish.minutes
    ? worked
    : `${worked} — ${formatAmount(finish.credited, 'minutes')} counted, the rest was more than you owed`;
}

export const weekdayLabels: Readonly<Record<Weekday, string>> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

export const guardKindLabels: Readonly<Record<RuleGuard['kind'], string>> = {
  'weekly-window': 'Days and times',
  'date-range': 'Dates',
  'daily-cap': 'Games a day',
};

/** What the effect does, as the form offers it: "When this holds, it …". */
export const guardEffectLabels: Readonly<Record<GuardEffect, string>> = {
  block: 'does not count',
  allow: 'is the only time it counts',
  double: 'counts double',
};

/** "Mon–Fri", "Mon, Wed, Fri": runs of days as a person would write them. */
export function describeWeekdays(days: readonly Weekday[]): string {
  const week = weekdaySchema.options;
  const chosen = week.filter((day) => days.includes(day));
  if (chosen.length === week.length) {
    return 'every day';
  }
  const runs: { first: Weekday; last: Weekday }[] = [];
  let previous = -2;
  for (const [index, day] of week.entries()) {
    if (!chosen.includes(day)) {
      continue;
    }
    const open = index === previous + 1 ? runs.at(-1) : undefined;
    if (open === undefined) {
      runs.push({ first: day, last: day });
    } else {
      open.last = day;
    }
    previous = index;
  }
  return runs
    .map(({ first, last }) =>
      first === last ? weekdayLabels[first] : `${weekdayLabels[first]}–${weekdayLabels[last]}`,
    )
    .join(', ');
}

const localDate = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' });

/** A guard's `YYYY-MM-DD`, read as a plain day rather than an instant. */
export function formatLocalDate(date: string): string {
  return localDate.format(new Date(`${date}T00:00:00`));
}

/** "Doesn’t count Mon–Fri 09:00–17:00": a guard as a sentence about the rule. */
export function describeGuard(guard: RuleGuard): string {
  const lead = {
    block: 'Doesn’t count',
    allow: 'Counts only',
    double: 'Counts double',
  }[guard.effect];
  switch (guard.kind) {
    case 'weekly-window':
      return `${lead} ${describeWeekdays(guard.days)} ${guard.start}–${guard.end}`;
    case 'date-range':
      return guard.from === guard.to
        ? `${lead} on ${formatLocalDate(guard.from)}`
        : `${lead} from ${formatLocalDate(guard.from)} to ${formatLocalDate(guard.to)}`;
    case 'daily-cap': {
      const games = guard.gamesPerDay === 1 ? 'game' : 'games';
      return `${lead} after ${String(guard.gamesPerDay)} ${games} a day`;
    }
  }
}

export const wagerStatusLabels: Readonly<Record<WagerStatus, string>> = {
  proposed: 'Waiting for an answer',
  active: 'On: waiting for the next game',
  settled: 'Settled',
  declined: 'Declined',
  cancelled: 'Called off',
};

export const wagerActionLabels: Readonly<Record<WagerAction, string>> = {
  accept: 'Accept',
  decline: 'Decline',
  cancel: 'Call off',
};

const wagerOutcomeLabels: Readonly<Record<GameResult, string>> = {
  win: 'You won',
  loss: 'You lost',
  draw: 'Drawn, nothing owed',
};

const oppositeResult: Readonly<Record<GameResult, GameResult>> = {
  win: 'loss',
  loss: 'win',
  draw: 'draw',
};

/** "Offered by Bob", "With Bob": who the wager is between, from the reader's side. */
export function describeWagerParties(wager: WagerResponse): string {
  if (wager.role === 'counterparty') {
    return `Offered by ${wager.proposer.displayName}`;
  }
  return wager.counterparty === null
    ? 'Opponent not on Gamble Chess'
    : `With ${wager.counterparty.displayName}`;
}

/** A settled wager's outcome as the reader lived it: the recorded result is the proposer's. */
export function describeWagerOutcome(wager: WagerResponse, outcome: GameResult): string {
  return wagerOutcomeLabels[wager.role === 'proposer' ? outcome : oppositeResult[outcome]];
}
